import React from 'react';
import styled from 'styled-components';
import {Checkbox} from '@forecast-it/design-system';
import {D} from './design-tokens';
import {KeyValueTable} from './KeyValueTable';
import {KeyValueForm} from './KeyValueForm';
import type {AffectedItem, EntityType, HistoryProp} from './actionCardTypes';
import Util from '../../forecast-app/shared/util/util';
import {EVENT_ID, dispatch} from '../../containers/event_manager';

const Row = styled.div<{$accentBorder: boolean}>`
	display: flex;
	gap: 12px;
	align-items: center;
	padding: 8px 16px;
	background: ${D.bg};
	border: 1px solid ${D.assistantBubbleBorder};
	${({$accentBorder}) => $accentBorder && `border-left: 4px solid ${D.v2.accent};`}
	border-radius: 4px;
`;

const Content = styled.div`
	flex: 1 0 0;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const Title = styled.div`
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0.15px;
	color: ${D.v2.textPrimary};
`;

const Desc = styled.div`
	font-size: 14px;
	font-weight: 350;
	line-height: 20px;
	letter-spacing: 0.15px;
	color: ${D.v2.textPrimary};
`;

const ViewLink = styled.button`
	border: none;
	background: none;
	cursor: pointer;
	color: ${D.v2.accent};
	font-size: 14px;
	font-weight: 500;
	padding: 0 4px;
	align-self: center;
`;

export function ActionItemRow({
	item,
	checked,
	disabled,
	onToggle,
	showView,
	history,
	entityType,
	edits,
	onEdit,
	editsReadOnly,
	hideCheckbox,
	accentBorder = true,
	fieldOverrideErrors,
}: {
	item: AffectedItem;
	checked: boolean;
	disabled: boolean;
	onToggle: (id: string) => void;
	showView?: boolean;
	history?: HistoryProp;
	entityType?: EntityType;
	edits?: Record<string, unknown>;
	onEdit?: (entityId: string, fieldKey: string, value: unknown) => void;
	editsReadOnly?: boolean;
	hideCheckbox?: boolean;
	accentBorder?: boolean;
	fieldOverrideErrors?: Record<string, string>;
}): JSX.Element {
	const kvRows = item.kv ? Object.entries(item.kv).map(([key, value]) => ({key, value})) : [];
	const viewUrl = item.view_url;
	// For a task, open the task modal overlaid on the current page via the canonical
	// helper (works from any page, swaps a task modal already open, refetches for fresh
	// state). `originalId` is the companyTaskId the modal keys on. We deliberately do NOT
	// use `view_url` for tasks — the backend emits a relative `card/{id}` path that matches
	// no app route. Non-task entities have no task modal, so fall back to view_url.
	const taskId = item.originalId ?? item.id;
	const canOpenTask = entityType === 'task' && taskId != null && /^\d+$/.test(String(taskId));
	const canView = !!history && (canOpenTask || !!viewUrl);
	const onView = (): void => {
		if (!history) return;
		if (canOpenTask) {
			// Highlight + scroll the task on the board (if mounted) on View,
			// same as on plan approval — not only when the plan is confirmed.
			dispatch(EVENT_ID.HIGHLIGHT_TASKS, [String(taskId)]);
			Util.showTaskModal(taskId, history);
		} else if (viewUrl) {
			history.push(viewUrl);
		}
	};
	return (
		<Row $accentBorder={accentBorder}>
			{!hideCheckbox && <Checkbox checked={checked} disabled={disabled} onChange={() => onToggle(item.id)} />}
			<Content>
				<Title>{item.title}</Title>
				<Desc>{item.description}</Desc>
				{kvRows.length > 0 && <KeyValueTable rows={kvRows} />}
				{item.fields && item.fields.length > 0 && (
					<KeyValueForm
						fields={item.fields}
						edits={edits ?? {}}
						onEdit={(k, v) => onEdit && onEdit(item.id, k, v)}
						readOnly={editsReadOnly}
						fieldOverrideErrors={fieldOverrideErrors}
					/>
				)}
			</Content>
			{showView && canView && (
				<ViewLink type="button" onClick={onView}>
					View
				</ViewLink>
			)}
		</Row>
	);
}
